<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { hall } from '@/hall'
import defaultGameIcon from '@/assets/hall/game_icon_default.webp'
import { ServerDefine } from '@/config/ServerDefine'

const props = defineProps({
  title: String,
  icon: String,
  gameType: {
    type: Number,
    required: true,
  },
})

interface GameDisplay {
  id: number
  name: string
  icon: string
  isTop: boolean
  isBuy: boolean
}

const displayGames = ref<GameDisplay[]>([])

const refreshGames = () => {
  // 0 is Providers.All
  const gameIds = hall.hallModule.getSubgameIDList(props.gameType, 0) || []
  console.log(
    `[GameCategorySection] refreshGames for ${props.title}, type: ${props.gameType}, gameIds:`,
    gameIds,
  )
  displayGames.value = gameIds.map((id) => {
    let info = hall.hallModule.getSubgameInfo(id)
    if (!info) {
      info = { gid: id, Name: hall.hallModule.getSubgameName(id) }
    }
    const gid = info.gid || id
    return {
      id: gid,
      name: info.Name || info.name || 'Unknown',
      icon: ServerDefine.GAME_ICON_URL + gid + '.png',
      isTop: hall.hallModule.isHotGame(id),
      isBuy: false,
    }
  })
}

const scrollContainer = ref<HTMLElement | null>(null)

const scrollLeft = () => {
  if (scrollContainer.value) {
    scrollContainer.value.scrollBy({ left: -200, behavior: 'smooth' })
  }
}

const scrollRight = () => {
  if (scrollContainer.value) {
    scrollContainer.value.scrollBy({ left: 200, behavior: 'smooth' })
  }
}

const openGame = (gameId: number) => {
  hall.hallModule.postSubgameFree(gameId)
}

const handleImageError = (e: Event) => {
  const target = e.target as HTMLImageElement
  target.onerror = null
  target.src = defaultGameIcon
}

onMounted(() => {
  refreshGames()
  hall.eventDispatcher.on(hall.EventDefine.RefreshSubgameList, refreshGames)
})

onUnmounted(() => {
  hall.eventDispatcher.off(hall.EventDefine.RefreshSubgameList, refreshGames)
})
</script>

<template>
  <section class="game-category-section">
    <div class="section-title">
      <div class="category-title">
        <span v-if="icon" class="cat-icon">{{ icon }}</span>
        {{ title }}
      </div>
      <div class="controls">
        <button class="ctrl-btn" @click="scrollLeft">&lt;</button>
        <button class="ctrl-btn" @click="scrollRight">&gt;</button>
        <button class="ctrl-btn all-btn">All</button>
      </div>
    </div>

    <div class="games-scroll-container" ref="scrollContainer">
      <div class="games-grid">
        <div
          v-for="game in displayGames"
          :key="game.id"
          class="game-card cursor-pointer"
          @click="openGame(game.id)"
        >
          <img
            :src="game.icon || defaultGameIcon"
            class="game-img"
            :alt="game.name"
            @error="handleImageError"
          />
          <span v-if="game.isTop" class="tag top">TOP</span>
          <span v-if="game.isBuy" class="tag buy">⭐ BUY</span>
        </div>
        <div v-if="displayGames.length === 0" class="no-games-placeholder">
          No games currently available in this category.
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.game-category-section {
  padding: 0 10px;
  margin-bottom: 20px;
}

.section-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.category-title {
  font-size: 1.2rem;
  font-weight: bold;
}

.cat-icon {
  margin-right: 5px;
}

.controls {
  display: flex;
  gap: 5px;
}

.ctrl-btn {
  background: var(--theme-tab-inactive);
  border: none;
  color: #aaa;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
}

.all-btn {
  background: #444;
}

.games-scroll-container {
  position: relative;
  width: 100%;
  overflow-x: auto;
  padding-bottom: 10px;
  scrollbar-width: none; /* Firefox */
}

.games-scroll-container::-webkit-scrollbar {
  display: none; /* Chrome/Safari */
}

.games-grid {
  display: grid;
  grid-template-rows: repeat(2, 1fr);
  grid-auto-flow: column;
  grid-auto-columns: calc((100vw - 20px - 30px) / 4);
  gap: 10px;
  min-height: 200px;
}

.game-card {
  border-radius: 12px;
  position: relative;
  overflow: hidden;
}

.game-img {
  width: 100%;
  height: auto;
  display: block;
}

.tag {
  position: absolute;
  font-size: 0.6rem;
  font-weight: 800;
  padding: 2px 6px;
  border-radius: 4px;
  z-index: 2;
}

.tag.top {
  top: 5px;
  left: 5px;
  background: #ff4500;
  color: white;
}

.tag.buy {
  top: 5px;
  right: 5px;
  background: var(--theme-accent);
  color: #000;
  border-radius: 10px;
}

.cursor-pointer {
  cursor: pointer;
}

/* ===== DESKTOP: bigger cards, single row ===== */
@media (min-width: 768px) {
  .game-category-section {
    padding: 0 14px;
    margin-bottom: 28px;
  }

  .category-title {
    font-size: 1.3rem;
  }

  /* Single row, larger cards */
  .games-grid {
    /* Single row on desktop */
    grid-template-rows: 1fr;
    /* Each card ~160px wide → shows ~8 at a time on 1280px screen minus 80px sidebar */
    grid-auto-columns: minmax(140px, 170px);
    gap: 12px;
    min-height: unset;
  }

  .game-card {
    border-radius: 14px;
  }

  .game-img {
    /* Fixed height for uniform look */
    height: 150px;
    width: 100%;
    object-fit: cover;
  }

  .tag {
    font-size: 0.65rem;
    padding: 3px 7px;
  }
}

@media (min-width: 1200px) {
  .games-grid {
    grid-auto-columns: minmax(155px, 185px);
  }

  .game-img {
    height: 170px;
  }
}
</style>
